import {
  Box,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Portal,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { MdExpandLess, MdExpandMore, MdDragHandle } from "react-icons/md";
import { Edge, Node } from "reactflow";

import { useAppSelector } from "@/reduxHooks";

import {
  currentReferenceRunId,
  currentWfActiveUser,
  currentWorkflowId,
  currentWorkflowRunId,
  currentWorkflowVersionTagId,
  workflowRunningStatus,
  workflowStatus,
} from "../../redux/workflow-slice";
import { NodeType } from "../../types";

const DEBUGGER_STORAGE_KEY = "workflow-debugger-expanded";
const DEBUGGER_POSITION_KEY = "workflow-debugger-position";

interface DebugInfo {
  label: string;
  value: number | string | null | undefined;
}

interface Position {
  x: number;
  y: number;
}

const WfDebugger = ({
  nodes,
  edges,
}: {
  nodes: Node<any, string | undefined>[];
  edges: Edge[];
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem(DEBUGGER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  });
  const [position, setPosition] = useState<Position>(() => {
    const stored = localStorage.getItem(DEBUGGER_POSITION_KEY);
    return stored ? JSON.parse(stored) : { x: 20, y: 20 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null
  );

  const workflowId = useAppSelector(currentWorkflowId);
  const workflowRunId = useAppSelector(currentWorkflowRunId);
  const referenceRunId = useAppSelector(currentReferenceRunId);
  const workflowRunStatus = useAppSelector(workflowRunningStatus);
  const currentUser = useAppSelector(currentWfActiveUser);
  const workflowVersionTagId = useAppSelector(currentWorkflowVersionTagId);
  const wfNodeStatuses = useAppSelector(workflowStatus)?.workflowNodeStatus;

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const valueColor = useColorModeValue("gray.800", "gray.200");

  useEffect(() => {
    localStorage.setItem(DEBUGGER_STORAGE_KEY, JSON.stringify(isExpanded));
  }, [isExpanded]);

  useEffect(() => {
    localStorage.setItem(DEBUGGER_POSITION_KEY, JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (mouseDownEvent: React.MouseEvent) => {
    const target = mouseDownEvent.target as HTMLElement;
    if (!target.closest(".drag-handle")) return;

    setIsDragging(true);
    setDragStart({
      x: mouseDownEvent.clientX - position.x,
      y: mouseDownEvent.clientY - position.y,
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!dragStart) return;

      setPosition({
        x: moveEvent.clientX - dragStart.x,
        y: moveEvent.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (import.meta.env.VITE_SHOW_DEBUGGER !== "true") {
    return null;
  }

  const debugInfo: DebugInfo[] = [
    { label: "Workflow ID", value: workflowId },
    { label: "Workflow Run ID", value: workflowRunId },
    { label: "Workflow Status", value: workflowRunStatus },
    { label: "Reference Run ID", value: referenceRunId },
    { label: "Workflow Version Tag ID", value: workflowVersionTagId },
    { label: "Nodes - Edges", value: `${nodes.length} - ${edges.length}` },
    {
      label: "Is current user",
      value: JSON.stringify(currentUser?.isCurrentUser),
    },
    { label: "Current User", value: currentUser?.user?.displayName },
  ];

  return (
    <Portal>
      <Box
        pos="fixed"
        zIndex={1000}
        top={`${position.y}px`}
        left={`${position.x}px`}
        w={isExpanded ? "500px" : "auto"}
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        shadow="md"
        transition="all 0.2s"
      >
        <Flex
          className="drag-handle"
          align="center"
          justify="space-between"
          p={2}
          borderColor={borderColor}
          borderBottom={isExpanded ? "1px" : "none"}
          cursor={isDragging ? "grabbing" : "grab"}
          onMouseDown={handleMouseDown}
        >
          <Flex align="center" gap={2}>
            <MdDragHandle className="text-gray-500" />
            <Text fontSize="sm" fontWeight="medium">
              Workflow Debugger
            </Text>
          </Flex>
          <IconButton
            aria-label={isExpanded ? "Collapse" : "Expand"}
            icon={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
            onClick={() => setIsExpanded(!isExpanded)}
            size="sm"
            variant="ghost"
          />
        </Flex>
        {isExpanded && (
          <Box>
            <Tabs size={"sm"}>
              <TabList>
                <Tab>Workflow Info</Tab>
                <Tab>Node Status</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  {debugInfo.map((info, index) => (
                    <Flex
                      key={info.label}
                      direction="column"
                      mb={index !== debugInfo.length - 1 ? 2 : 0}
                    >
                      <Text color={labelColor} fontSize="xs">
                        {info.label}
                      </Text>
                      <Text
                        color={valueColor}
                        fontSize="sm"
                        fontWeight="medium"
                        noOfLines={1}
                      >
                        {info.value || "-"}
                      </Text>
                    </Flex>
                  ))}
                </TabPanel>
                <TabPanel>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Node Name</Th>
                          <Th>UI ID</Th>
                          <Th>Locked</Th>
                          <Th>Status</Th>
                          <Th>Has output</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {nodes.map((node) => {
                          const nodeStatus = wfNodeStatuses?.find(
                            (status) => status.uiNodeId === node.id
                          );
                          return (
                            <Tr key={node.id}>
                              <Td className="text-xs truncate max-w-[100px]">
                                {node.data.name}
                              </Td>
                              <Td className="text-xs truncate max-w-[100px]">
                                {node.id}
                              </Td>
                              <Td>{nodeStatus?.isLocked ? "🔒" : "-"}</Td>
                              <Td>
                                {nodeStatus?.nodeStatus ||
                                  node.data.nodeStatus ||
                                  "-"}
                              </Td>
                              <Td>
                                {node.data?.isOutputDataAvailable ? "✅" : "❌"}
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        )}
      </Box>
    </Portal>
  );
};

export default WfDebugger;
